/**
 * Behavior Metrics Service
 * Handles API calls for behavior metrics analytics
 */

/**
 * Fetch behavior metrics from the API
 */
export const fetchBehaviorMetrics = async (
  filters = {},
  apiUrl = process.env.REACT_APP_API_URL || 'https://devserver.spatialgrid.ai'
) => {
  try {
    const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
    
    if (!token) {
      throw new Error('No authentication token found. Please login again.');
    }
    
    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    };

  // Build query string from filters
    const queryParams = new URLSearchParams();
    // Only append if values are explicitly provided
    if (filters.startDate && filters.startDate.trim()) queryParams.append('startDate', filters.startDate);
    if (filters.endDate && filters.endDate.trim()) queryParams.append('endDate', filters.endDate);
    if (filters.behaviorType && filters.behaviorType.trim()) queryParams.append('behaviorType', filters.behaviorType);
    if (filters.page && filters.page.trim()) queryParams.append('page', filters.page);
    if (filters.experienceId && filters.experienceId.trim()) queryParams.append('experienceId', filters.experienceId);

    // Only add ? if there are actual query parameters
    const queryString = queryParams.toString();
    const url = queryString 
      ? `${apiUrl}/api/v1/analytics/behavior?${queryString}`
      : `${apiUrl}/api/v1/analytics/behavior`;

    const response = await fetch(
      url,
      {
        method: 'GET',
        headers,
        credentials: 'include',
      }
    );

    if (!response.ok) {
      if (response.status === 401) {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('token');
        throw new Error('Session expired. Please login again.');
      }
      throw new Error(`Failed to fetch behavior metrics: ${response.status} ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching behavior metrics:', error);
    throw error;
  }
};

/**
 * Fetch specific behavior type metrics (rage clicks, dead clicks, etc.)
 */
export const fetchBehaviorTypeMetrics = async (
  behaviorType,
  apiUrl = process.env.REACT_APP_API_URL || 'https://devserver.spatialgrid.ai'
) => {
  try {
    const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
    
    if (!token) {
      throw new Error('No authentication token found. Please login again.');
    }
    
    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    };

    const response = await fetch(
      `${apiUrl}/api/v1/analytics/behavior/type/${behaviorType}`,
      {
        method: 'GET',
        headers,
        credentials: 'include',
      }
    );

    if (!response.ok) {
      if (response.status === 401) {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('token');
        throw new Error('Session expired. Please login again.');
      }
      throw new Error(`Failed to fetch ${behaviorType} metrics: ${response.status} ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error(`Error fetching ${behaviorType} metrics:`, error);
    throw error;
  }
};

/**
 * Fetch behavior metrics summary
 */
export const fetchBehaviorSummary = async (
  apiUrl = process.env.REACT_APP_API_URL || 'https://devserver.spatialgrid.ai'
) => {
  try {
    const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
    
    if (!token) {
      throw new Error('No authentication token found. Please login again.');
    }
    
    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    };

    const response = await fetch(
      `${apiUrl}/api/v1/analytics/behavior/summary`,
      {
        method: 'GET',
        headers,
        credentials: 'include',
      }
    );

    if (!response.ok) {
      if (response.status === 401) {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('token');
        throw new Error('Session expired. Please login again.');
      }
      throw new Error(`Failed to fetch behavior summary: ${response.status} ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching behavior summary:', error);
    throw error;
  }
};

/**
 * Post behavior metrics (send from frontend)
 */
export const postBehaviorMetrics = async (
  metricsData,
  apiUrl = process.env.REACT_APP_API_URL || 'https://devserver.spatialgrid.ai'
) => {
  try {
    const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
    
    if (!token) {
      throw new Error('No authentication token found. Please login again.');
    }
    
    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    };

    const response = await fetch(`${apiUrl}/api/v1/analytics/behavior`, {
      method: 'POST',
      headers,
      body: JSON.stringify(metricsData),
      credentials: 'include',
    });

    if (!response.ok) {
      if (response.status === 401) {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('token');
        throw new Error('Session expired. Please login again.');
      }
      throw new Error(`Failed to post behavior metrics: ${response.status} ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error posting behavior metrics:', error);
    throw error;
  }
};

/**
 * Transform API response format to flat metrics array
 * Converts {behavior: {rageClicks, deadClicks, etc}} to array of metric objects
 */
export const transformBehaviorResponse = (apiResponse) => {
  if (!apiResponse || !apiResponse.behavior) {
    return [];
  }

  const { behavior } = apiResponse;
  const metrics = [];

  // Transform rageClicks
  if (behavior.rageClicks && behavior.rageClicks.details) {
    behavior.rageClicks.details.forEach(detail => {
      metrics.push({
        behaviorType: 'rage_click',
        sessionId: detail.sessionId,
        page: detail.page,
        clickCount: detail.clickCount,
        timeSpan: detail.timeSpan,
        details: detail.details,
        timestamp: detail.timestamp,
      });
    });
  }

  // Transform deadClicks
  if (behavior.deadClicks && behavior.deadClicks.details) {
    behavior.deadClicks.details.forEach(detail => {
      metrics.push({
        behaviorType: 'dead_click',
        sessionId: detail.sessionId,
        page: detail.page,
        clickCount: detail.clickCount,
        timeSpan: detail.timeSpan,
        details: detail.details,
        timestamp: detail.timestamp,
      });
    });
  }

  // Transform uTurns
  if (behavior.uTurns && behavior.uTurns.details) {
    behavior.uTurns.details.forEach(detail => {
      metrics.push({
        behaviorType: 'u_turn',
        sessionId: detail.sessionId,
        page: detail.page,
        clickCount: detail.clickCount,
        timeSpan: detail.timeSpan,
        details: detail.details,
        timestamp: detail.timestamp,
      });
    });
  }

  // Transform quickBacks
  if (behavior.quickBacks && behavior.quickBacks.details) {
    behavior.quickBacks.details.forEach(detail => {
      metrics.push({
        behaviorType: 'quick_back',
        sessionId: detail.sessionId,
        page: detail.page,
        clickCount: detail.clickCount,
        timeSpan: detail.timeSpan,
        details: detail.details,
        timestamp: detail.timestamp,
      });
    });
  }

  return metrics;
};

/**
 * Calculate behavior statistics
 */
export const calculateBehaviorStats = (metricsData) => {
  if (!metricsData || metricsData.length === 0) {
    return {
      totalRageClicks: 0,
      totalDeadClicks: 0,
      totalUTurns: 0,
      totalQuickBacks: 0,
      topElementsWithRageClicks: [],
      mostUTurnPages: [],
      averageRageClickCount: 0,
    };
  }

  // Count behavior types
  const rageClicks = metricsData.filter(m => m.behaviorType === 'rage_click');
  const deadClicks = metricsData.filter(m => m.behaviorType === 'dead_click');
  const uTurns = metricsData.filter(m => m.behaviorType === 'u_turn');
  const quickBacks = metricsData.filter(m => m.behaviorType === 'quick_back');

  // Top elements with rage clicks
  const elementRageCounts = {};
  rageClicks.forEach(rc => {
    const elementId = rc.details?.elementId || 'unknown';
    elementRageCounts[elementId] = (elementRageCounts[elementId] || 0) + rc.clickCount;
  });

  const topElementsWithRageClicks = Object.entries(elementRageCounts)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 5)
    .map(([element, count]) => ({
      element,
      rageClickCount: count,
    }));

  // Most U-Turn pages
  const pageCounts = {};
  uTurns.forEach(ut => {
    const page = ut.page || 'unknown';
    pageCounts[page] = (pageCounts[page] || 0) + 1;
  });

  const mostUTurnPages = Object.entries(pageCounts)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 5)
    .map(([page, count]) => ({
      page,
      uTurnCount: count,
    }));

  // Average rage click count
  const averageRageClickCount = rageClicks.length > 0
    ? rageClicks.reduce((sum, rc) => sum + (rc.clickCount || 0), 0) / rageClicks.length
    : 0;

  return {
    totalRageClicks: rageClicks.length,
    totalDeadClicks: deadClicks.length,
    totalUTurns: uTurns.length,
    totalQuickBacks: quickBacks.length,
    topElementsWithRageClicks,
    mostUTurnPages,
    averageRageClickCount: parseFloat(averageRageClickCount.toFixed(2)),
  };
};

/**
 * Get page behavior metrics for a specific page
 */
export const getPageBehaviorMetrics = (metricsData, page) => {
  const pageMetrics = metricsData.filter(m => m.page === page);
  return calculateBehaviorStats(pageMetrics);
};

/**
 * Calculate behavior stats for a specific behavior type
 * Note: metricsData should already be filtered by behaviorType
 */
export const calculateTypeSpecificStats = (metricsData, behaviorType) => {
  if (!metricsData || metricsData.length === 0) {
    return {
      totalCount: 0,
      topElements: [],
      topPages: [],
      averageCount: 0,
    };
  }

  // Data is already filtered, no need to filter again
  const filteredData = metricsData;

  // For elements (all behavior types can have elements)
  const elementCounts = {};
  filteredData.forEach(item => {
    // Try to get element from details
    let elementId = item.details?.elementId;
    
    if (!elementId) {
      // For u_turns, try intermediaryPage as identifier
      if (behaviorType === 'u_turn' && item.details?.intermediaryPage) {
        elementId = `via ${item.details.intermediaryPage}`;
      } else if (behaviorType === 'quick_back') {
        elementId = 'Back Navigation';
      } else {
        elementId = 'unknown element';
      }
    }
    
    elementCounts[elementId] = (elementCounts[elementId] || 0) + 1;
  });

  const topElements = Object.entries(elementCounts)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 5)
    .map(([element, count]) => ({
      element,
      count,
    }));

  // For pages
  const pageCounts = {};
  filteredData.forEach(item => {
    const page = item.page || 'unknown';
    pageCounts[page] = (pageCounts[page] || 0) + 1;
  });

  const topPages = Object.entries(pageCounts)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 5)
    .map(([page, count]) => ({
      page,
      count,
    }));

  // Average clicks or count
  const averageCount = filteredData.length > 0
    ? filteredData.reduce((sum, item) => sum + (item.clickCount || 1), 0) / filteredData.length
    : 0;

  return {
    totalCount: filteredData.length,
    topElements,
    topPages,
    averageCount: parseFloat(averageCount.toFixed(2)),
  };
};

/**
 * Format behavior type for display
 */
export const formatBehaviorType = (type) => {
  const typeMap = {
    rage_click: 'Rage Clicks',
    dead_click: 'Dead Clicks',
    u_turn: 'U-Turns',
    quick_back: 'Quick Backs',
  };
  return typeMap[type] || type;
};

/**
 * Get icon for behavior type
 */
export const getBehaviorIcon = (type) => {
  const iconMap = {
    rage_click: '😡',
    dead_click: '💀',
    u_turn: '📍',
    quick_back: '⏪',
  };
  return iconMap[type] || '📊';
};

export default {
  fetchBehaviorMetrics,
  fetchBehaviorTypeMetrics,
  fetchBehaviorSummary,
  postBehaviorMetrics,
  transformBehaviorResponse,
  calculateBehaviorStats,
  calculateTypeSpecificStats,
  getPageBehaviorMetrics,
  formatBehaviorType,
  getBehaviorIcon,
};
